import { descendantProjects } from './project-tree';
/**
 * Everything that must go when you delete something.
 *
 * Deletion here was only ever half-done, and the confirmation dialog said so
 * out loud without it being true. "Delete Project" warned *"this will also
 * delete N sub-projects and all their tasks"* and *"N tasks will be
 * deleted"* — but `deleteProjectWithCascade` removed project rows only.
 * Every task under the deleted subtree survived, still carrying a
 * `project_id` that no longer resolved. Those tasks are invisible in the UI
 * (every list is reached through a project) yet they still count: they were
 * scheduled, they appeared in analytics totals, and they rode along in every
 * sync payload forever.
 *
 * Deleting a parent task had the same shape — its subtasks kept a
 * `parent_task_id` pointing at nothing.
 *
 * So both closures are computed here, once, and both are cycle-safe: a cycle
 * cannot be made through the UI but can arrive through an import, and a hang
 * while deleting is worse than a truncated cascade.
 */
/** A task plus every subtask beneath it, at any depth. Deepest first. */
export function taskDeletionSet(tasks, rootId) {
    const childrenOf = new Map();
    for (const t of tasks) {
        if (!t.parent_task_id)
            continue;
        const siblings = childrenOf.get(t.parent_task_id) ?? [];
        siblings.push(t.id);
        childrenOf.set(t.parent_task_id, siblings);
    }
    const ordered = [];
    const seen = new Set();
    const walk = (id) => {
        if (seen.has(id))
            return;
        seen.add(id);
        for (const child of childrenOf.get(id) ?? [])
            walk(child);
        ordered.push(id); // children land before their parent
    };
    walk(rootId);
    return ordered;
}
/**
 * A project, its whole subtree, and every task inside any of them.
 *
 * Order matters for tombstones: a device that receives these deletions
 * should see children before parents, so it never briefly holds an orphan.
 */
export function projectDeletionSet(projects, tasks, rootId) {
    const descendants = descendantProjects(projects, rootId);
    // Deepest first: descendantProjects is breadth-first from the root, so
    // reversing puts the furthest leaves at the front.
    const projectIds = [...descendants.map((p) => p.id).reverse(), rootId];
    const doomedProjects = new Set(projectIds);
    // Top-level tasks first, then expand each into its subtask closure. Going
    // through taskDeletionSet means a subtask whose parent lives in the same
    // subtree is not visited twice.
    const taskIds = [];
    const added = new Set();
    for (const task of tasks) {
        if (task.parent_task_id)
            continue; // reached via its parent
        if (!doomedProjects.has(task.project_id))
            continue;
        for (const id of taskDeletionSet(tasks, task.id)) {
            if (added.has(id))
                continue;
            added.add(id);
            taskIds.push(id);
        }
    }
    // A subtask whose own project_id is in the subtree but whose parent is NOT
    // (data that shouldn't exist, but does after older orphaning bugs) still
    // has to go, or deleting the project leaves it behind again.
    for (const task of tasks) {
        if (!task.parent_task_id)
            continue;
        if (!doomedProjects.has(task.project_id))
            continue;
        if (added.has(task.id))
            continue;
        added.add(task.id);
        taskIds.push(task.id);
    }
    return {
        projectIds,
        taskIds,
        taskCount: taskIds.length,
        subprojectCount: descendants.length,
    };
}
/**
 * Tasks pointing at a project that no longer exists.
 *
 * Diagnostic for data written before deletion cascaded properly — there is
 * no way to reach these in the UI, because every task list is entered
 * through a project.
 */
export function orphanedTasks(projects, tasks) {
    const known = new Set(projects.map((p) => p.id));
    return tasks.filter((t) => t.project_id && !known.has(t.project_id));
}
/**
 * The habit instances that go with deleted tasks, as their storage keys.
 *
 * A habit's completion history is its HabitInstances (`habit_id:date`).
 * Deleting the habit has to delete them too, or they linger as orphans that
 * still count as active days and completions. The assistant did this for a
 * task delete, but not when the habit went with a deleted project; the app
 * did it for neither — so the same instruction left different data
 * depending on who carried it out. Both now take the list from here.
 */
export function habitInstanceKeysFor(instances, taskIds) {
    const doomed = new Set(taskIds);
    return instances.filter((i) => doomed.has(i.habit_id)).map((i) => `${i.habit_id}:${i.date}`);
}
//# sourceMappingURL=deletion.js.map